import { observer } from 'mobx-react';
import * as React from 'react';
import Skeleton from 'react-loading-skeleton';
import { BUTTON_TITLE } from 'src/constants/main';
import { USER_PERMISSIONS } from 'src/constants/user';
import { appStore } from 'src/mobx/appStore';
import { branchStore } from 'src/mobx/branchStore';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import logger from 'src/services/logger';
import { IBranch } from 'src/types/branches';
import { booleanToNumber, hasPermissions, isTouchDevice } from 'src/utils/helpers';
import CustomTooltip, { TOOLTIP_DURATION, TOOLTIP_POSITION, TOOLTIP_TRIGGERS } from 'src/view/components/Controls/CustomTooltip/CustomTooltip';
import Switcher from 'src/view/components/Controls/Switcher/Switcer';
import ModalMessage from 'src/view/components/ModalMessage/ModalMessage';
import ModalPopup from 'src/view/components/ModalPopup/ModalPopup';
import { IMessageModalOption } from '~/view/containers/Messages';
import './style.scss';

interface IProps {
  branch: IBranch;
  className?: string;
}

interface IState {
  status: boolean;
  tooltip: boolean;
  confirmBranchBusyText: string;
  isLoading: boolean;
}

@observer
export default class BranchBusy extends React.Component<IProps, IState> {
  switcher: Switcher | null = null;
  modal: ModalPopup | null = null;

  state: IState = {
    status: this.props.branch.is_busy,
    confirmBranchBusyText: localeStore.t('confirm_branch_busy_manual'),
    tooltip: false,
    isLoading: false,
  };

  componentDidMount = async () => {
    try {
      this.setState({
        isLoading: true,
      });
      const { msg } = await client.get(`/v3/branches/${this.props.branch.id}/busy-expire`);
      if (msg)
        this.setState({
          confirmBranchBusyText: msg,
          isLoading: false,
        });
    } catch (error: any) {
      this.setState({ isLoading: false });
      logger.log(error);
    }
  };

  componentDidUpdate = (props: IProps) => {
    if (this.props.branch.is_busy !== props.branch.is_busy) {
      this.setState({ status: this.props.branch.is_busy });
    }
  };

  toggleStatus = () => {
    if (!this.props.branch.is_busy) {
      this.changeModalVisibility(true);
      return;
    }
    this.changeStatus();
  };

  changeModalVisibility = (visible: boolean) => {
    if (visible) {
      this.modal?.openModalPopup();
    } else {
      if (this.switcher) {
        this.switcher.status = booleanToNumber(this.props.branch.is_busy);
      }
      this.modal?.closeModalPopup();
    }
  };

  changeStatus = async () => {
    const succesfully = await branchStore.setBranchBusy(this.props.branch.id, !this.props.branch.is_busy);
    if (!succesfully) this.setState({ tooltip: true });
  };

  get disabled(): boolean {
    return !hasPermissions(USER_PERMISSIONS.BRANCH_BUSY_MODE);
  }

  get tooltipTriggerType(): TOOLTIP_TRIGGERS {
    if (this.disabled) return isTouchDevice() ? TOOLTIP_TRIGGERS.CLICK : TOOLTIP_TRIGGERS.HOVER;
    else if (this.state.tooltip) return TOOLTIP_TRIGGERS.CLICK;
    else return TOOLTIP_TRIGGERS.NONE;
  }

  get messageOptions(): IMessageModalOption[] {
    return [
      {
        title: localeStore.t(BUTTON_TITLE.SET_BUSY),
        onPress: () => {
          this.setState({ status: true });
          this.changeStatus();
        },
        disabled: this.state.isLoading,
      },
      { title: localeStore.t(BUTTON_TITLE.CANCEL), appearance: 'tertiary' },
    ];
  }

  public render() {
    return (
      <div className={`d-flex align-items-center ${this.props.className ? this.props.className : ''}`}>
        {this.props.branch && localeStore.localizationLoaded && appStore.timestamp ? (
          <React.Fragment>
            <ModalPopup
              autoClose
              vertical="bottom"
              ref={(modal) => (this.modal = modal)}
              content={
                <div className="busy-modal d-flex flex-row justify-content-center">
                  <ModalMessage
                    closeModal={() => this.changeModalVisibility(false)}
                    title={localeStore.t('are_you_sure')}
                    message={this.state.confirmBranchBusyText}
                    iconClassName="color-gray2"
                    isLoading={this.state.isLoading}
                    options={this.messageOptions}
                  />
                  <div className="busy-modal__overlay" onClick={() => this.changeModalVisibility(false)}></div>
                </div>
              }
            >
              <div className="d-flex align-items-center">
                <CustomTooltip
                  triggerType={this.tooltipTriggerType}
                  position={TOOLTIP_POSITION.PORTAL}
                  trigger={this.state.tooltip}
                  disableTrigger={() => this.setState({ tooltip: false })}
                  message="no_permission"
                  duration={TOOLTIP_DURATION.EXTRA_LARGE}
                >
                  <Switcher
                    ref={(switcher) => (this.switcher = switcher)}
                    status={booleanToNumber(this.state.status)}
                    onChange={this.toggleStatus}
                    disabled={this.disabled}
                    activeClassName="bg-red"
                  />
                </CustomTooltip>
                <p className="ml-4 color-dark">{localeStore.t('busy')}</p>
              </div>
            </ModalPopup>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Skeleton width={50} height={30} />
            <Skeleton className="ml-4" width={60} height={20} />
          </React.Fragment>
        )}
      </div>
    );
  }
}
